import { useNavigate, useParams } from "react-router-dom";
import {
  useMyHands,
  useToggleHandInstall,
  useUninstallHand,
  type MyHand,
} from "../../hooks/useHands";

const MODEL_SHORT: Record<string, string> = {
  "claude-haiku-4-5-20251001": "Haiku",
  "claude-sonnet-4-6": "Sonnet",
  "claude-opus-4-7": "Opus",
};

export function HandsMyHands() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();

  const handsQ = useMyHands(workspaceId);
  const toggleMut = useToggleHandInstall(workspaceId);
  const uninstallMut = useUninstallHand(workspaceId);

  const hands = handsQ.data ?? [];
  const hasCredError = false; // placeholder — real check would hit /credentials endpoint

  const handleToggle = (h: MyHand) => {
    toggleMut.mutate({ installId: h.install_id, enabled: !h.enabled });
  };

  const handleUninstall = (h: MyHand) => {
    if (!confirm(`Uninstall "${h.name}"? This removes it from your workspace.`)) return;
    uninstallMut.mutate(h.install_id);
  };

  return (
    <div className="main-inner">
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
        <div className="greet">My Hands</div>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn primary"
          onClick={() => navigate(`/workspaces/${workspaceId}/hands/library`)}
        >
          + Browse Library
        </button>
      </div>
      <div className="greet-sub">
        Hands installed in your workspace. Enable or disable them per workspace without uninstalling.
      </div>

      {hasCredError && (
        <div
          style={{ background: "#fff3e0", border: "1px solid #ffe0b2", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#b76c00", marginTop: 16, display: "flex", alignItems: "center", gap: 8 }}
        >
          <span>⚠</span>
          <span>No API credential profile configured. Hands will use Xamun-managed credits. <a href={`/workspaces/${workspaceId}/settings`} style={{ color: "inherit", textDecoration: "underline" }}>Add BYOK credentials →</a></span>
        </div>
      )}

      {handsQ.isLoading ? (
        <div style={{ marginTop: 24 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ height: 56, borderRadius: 8, background: "var(--bg-soft)", marginBottom: 8, opacity: 0.6 }} />
          ))}
        </div>
      ) : hands.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "var(--text-3)" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>✦</div>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6, color: "var(--text-2)" }}>No hands installed yet</div>
          <div style={{ fontSize: 12, marginBottom: 20 }}>Browse the library to find and install AI agents.</div>
          <button
            type="button"
            className="btn primary"
            onClick={() => navigate(`/workspaces/${workspaceId}/hands/library`)}
          >
            Browse Library
          </button>
        </div>
      ) : (
        <div style={{ marginTop: 20, overflowX: "auto" }}>
          <table className="mh-table">
            <thead>
              <tr>
                <th>Hand</th>
                <th>Model</th>
                <th>Source</th>
                <th>Version</th>
                <th>Cost MTD</th>
                <th>Calls 30d</th>
                <th>Last used</th>
                <th>Enabled</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {hands.map((h) => (
                <HandRow
                  key={h.install_id}
                  hand={h}
                  onToggle={() => handleToggle(h)}
                  onUninstall={() => handleUninstall(h)}
                  toggling={toggleMut.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HandRow({
  hand,
  onToggle,
  onUninstall,
  toggling,
}: {
  hand: MyHand;
  onToggle: () => void;
  onUninstall: () => void;
  toggling: boolean;
}) {
  const versionBehind = hand.installed_version !== hand.latest_version;
  const modelLabel = MODEL_SHORT[hand.model] ?? hand.model;

  return (
    <tr style={{ opacity: hand.enabled ? 1 : 0.55 }}>
      <td>
        <div className="mh-hand-name">
          {hand.icon ? <span style={{ marginRight: 6 }}>{hand.icon}</span> : null}
          {hand.name}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>{hand.category}</div>
      </td>
      <td>
        <span className="pill blue" style={{ fontSize: 10 }}>{modelLabel}</span>
      </td>
      <td style={{ fontSize: 12, color: "var(--text-2)" }}>
        {hand.scope === "public" ? "Xamun" : hand.owner_name ?? "Company"}
      </td>
      <td>
        <span className="mh-hand-ver">v{hand.installed_version}</span>
        {versionBehind && (
          <span className="mh-update-badge">update</span>
        )}
      </td>
      <td style={{ fontSize: 12 }}>
        ${parseFloat(hand.cost_mtd || "0").toFixed(2)}
      </td>
      <td style={{ fontSize: 12 }}>
        {parseInt(hand.calls_30d || "0", 10).toLocaleString()}
      </td>
      <td style={{ fontSize: 12, color: "var(--text-3)" }}>
        {hand.last_used ? new Date(hand.last_used).toLocaleDateString() : "—"}
      </td>
      <td>
        <button
          type="button"
          className={`toggle-sw ${hand.enabled ? "on" : ""}`}
          onClick={onToggle}
          disabled={toggling}
          aria-label={hand.enabled ? "Disable" : "Enable"}
        />
      </td>
      <td>
        <button
          type="button"
          className="btn sm"
          style={{ color: "#c0392b", fontSize: 11 }}
          onClick={onUninstall}
        >
          Uninstall
        </button>
      </td>
    </tr>
  );
}
